import { useEffect } from 'react'
import { useEditor } from '@/state/store'

/** Global keyboard shortcuts for the editor. */
export function useKeyboard() {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement
      const typing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
      const s = useEditor.getState()

      // Read-only share view: allow only navigation (select / pan / deselect).
      // Mutation is already blocked at the store, but we suppress the shortcuts too.
      if (s.readOnly) {
        if (typing) return
        switch (e.key.toLowerCase()) {
          case 'v':
            s.setTool('select')
            break
          case 'h':
            s.setTool('pan')
            break
          case 'escape':
            s.setTool('select')
            s.select(null)
            break
        }
        return
      }

      // Undo / redo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) s.redo()
        else s.undo()
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault()
        s.redo()
        return
      }

      // Select all spaces on the active level
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        if (typing) return
        e.preventDefault()
        s.setTool('select')
        s.selectAllSpaces()
        return
      }

      // Copy / paste spaces. When typing, fall through so the browser copies/pastes text.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        if (typing) return
        if (s.selection?.kind === 'spaces') {
          e.preventDefault()
          s.copySelection()
        }
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
        if (typing) return
        e.preventDefault()
        s.setTool('select')
        s.paste()
        return
      }

      if (typing) return

      // Tool shortcuts
      switch (e.key.toLowerCase()) {
        case 'v':
          s.setTool('select')
          break
        case 'r':
          s.setTool('space-rect')
          break
        case 'p':
          s.setTool('space-poly')
          break
        case 'h':
          s.setTool('pan')
          break
        case 'd':
          s.setActiveOpeningPresetId('door-internal')
          s.setTool('opening')
          break
        case 'escape':
          s.setTool('select')
          s.select(null)
          break
        case '[':
        case ']': {
          // Rotate the selected prop by ±15° (single undo step each).
          const sel = s.selection
          if (sel?.kind !== 'prop') break
          e.preventDefault()
          const delta = e.key === '[' ? -15 : 15
          s.update((draft) => {
            const p = draft.levels.flatMap((l) => l.props).find((x) => x.id === sel.id)
            if (p) p.rotation = ((p.rotation + delta) % 360 + 360) % 360
          })
          break
        }
        case 'delete':
        case 'backspace': {
          const sel = s.selection
          if (sel?.kind === 'spaces') {
            e.preventDefault()
            const ids = new Set(sel.ids)
            s.update((draft) => {
              for (const lvl of draft.levels) {
                lvl.spaces = lvl.spaces.filter((sp) => !ids.has(sp.id))
              }
            })
            s.select(null)
          } else if (sel?.kind === 'prop') {
            e.preventDefault()
            s.update((draft) => {
              for (const lvl of draft.levels) {
                lvl.props = lvl.props.filter((p) => p.id !== sel.id)
              }
            })
            s.select(null)
          } else if (sel?.kind === 'opening') {
            e.preventDefault()
            s.update((draft) => {
              if (sel.ownerId === 'compound') {
                const cw = draft.plot.compoundWall
                if (cw) cw.openings = cw.openings.filter((o) => o.id !== sel.id)
              } else {
                for (const lvl of draft.levels) {
                  const sp = lvl.spaces.find((x) => x.id === sel.ownerId)
                  if (sp) sp.openings = sp.openings.filter((o) => o.id !== sel.id)
                }
              }
            })
            s.select(null)
          }
          break
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}
